import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Tag as BTag, Link as BLink, Message } from '@bedrock/components';
import Decimal from 'decimal.js';
import './index.less';
import BlockTitle from '../BlockTitle';
import BaseInfoForm from '../BaseInfoForm';
import TenderForm from '../TenderForm';
import DetailLayoutWithTabs from '../DetailLayoutWithTabs';
import EvaluationForm from '../EvaluationForm';
import DescriptionsFormItem from '@mpc/common/src/components/DescriptionsFormItem';
import { useBidingHistoryModal } from '@mpc/common/src/features/HistoryQuoteModal/bc';
import { NodeEnum } from 'containers/OutBidding/utils';
import { enums, outsourceCommonQueryTemporary, outsourceNewSupplierQueryVmdmBankAccount } from 'services/api';
import BankAccount from '@mpc/common/src/features/BankAccount';
import { getVendorStorage } from '@mpc/common/src/utils';
import { ChevronRight } from '@bedrock/icons-react';
import QuoteDetailField from '@mpc/common/src/features/Bidding/SupplierQuoteDrawer/QuoteDetailField';
import { useDemandOptions } from '@mpc/common';

const Template = (props: any) => {
  const { detail = {}, editFields = [], renderOpers, hasSubmitted, quoteData, canEditQuote } = props;
  const baseFormRef: any = useRef();
  const tenderFormRef: any = useRef();
  const evaluationFormRef: any = useRef();
  // 报价信息模块独立 form 实例（SFormItem 内部用 Form.Item，需要 Form 上下文）
  // 参照 NewPendingQuote 的写法：DescriptionsFormItem 必须在 <Form form={...}> 内
  const [quoteForm] = Form.useForm();
  const [isOverseaInvoice, setIsOverseaInvoice] = useState(0);
  const [invoiceBelongArea, setInvoiceBelongArea] = useState<number>();

  // 回显表单数据（quoteData.tabs + 暂存数据合并 + detail.bidding_suppliers）
  useEffect(() => {
    const fetchAndMerge = async () => {
      const patch: Record<string, any> = {};


      // 1. 合并 quoteData.tabs 与暂存数据（暂存数据优先）
      let tabs = quoteData?.tabs?.length ? [...quoteData.tabs] : [];

      try {
        const { vendorId, tenantCode } = getVendorStorage();
        if (vendorId && tenantCode && detail?.id) {
          const code = `${vendorId}_${tenantCode}_${detail.id}`;
          const res = await outsourceCommonQueryTemporary({
            type: 'outsource_bidding_quote',
            code,
          });
          if (res?.data) {
            const tempTabs = JSON.parse(res.data) as Record<string, any>[];
            if (Array.isArray(tempTabs) && tempTabs.length) {
              // 按 config_id 合并：暂存数据优先覆盖
              const mergedMap = new Map<number, Record<string, any>>();
              for (const tab of tabs) {
                if (tab.config_id != null) mergedMap.set(tab.config_id, tab);
              }
              for (const tab of tempTabs) {
                if (tab.config_id != null) mergedMap.set(tab.config_id, tab);
              }
              tabs = Array.from(mergedMap.values());
            }
          }
        }
      } catch (e) {
        console.warn('查询暂存数据失败，降级使用 quoteData', e);
      }

      if (tabs.length) {
        const total = tabs.reduce((sum, tab) => {
          const rows = tab.rows ?? [];
          return rows.reduce((s: Decimal, row: any) => {
            const v = row?.priceAmount;
            return v !== undefined && v !== null && v !== '' ? s.plus(new Decimal(v)) : s;
          }, sum);
        }, new Decimal(0));
        patch.quote_detail = tabs;
        patch.total_quoted = { value: total.toNumber(), currency: 'RMB' };
      }

      // 2. detail.bidding_suppliers[0] → 收款账户 + 发票 + 报价金额 + 报价文件
      const supplier = detail?.bidding_suppliers?.[0];
      if (supplier) {
        const quote = supplier.bidding_supplier_quotes?.[0];
        if (quote) {
          if (supplier.channel_supplier_bank) {
            patch.channel_supplier_bank_dto = supplier.channel_supplier_bank;
          }
          if (supplier.invoice_belong_area !== undefined) {
            patch.invoice_belong_area = supplier.invoice_belong_area;
            setInvoiceBelongArea(supplier.invoice_belong_area);
          }
          if (supplier.invoice_type !== undefined) patch.invoice_type = supplier.invoice_type;
          if (supplier.is_oversea_invoice !== undefined) {
            patch.is_oversea_invoice = supplier.is_oversea_invoice;
            setIsOverseaInvoice(supplier.is_oversea_invoice);
          }
          if (quote?.quote_price !== undefined) {
            patch.total_quoted = { value: quote.quote_price, currency: quote.currency ?? 'RMB' };
          }
          if (quote?.quote_price_attachment_dtos?.length) {
            patch.bid_attachs = quote.quote_price_attachment_dtos.map((a: any) => ({
              id: a.id,
              uid: a.blob?.id,
              name: a.blob?.filename,
              size: a.blob?.byte_size,
              status: 'done',
              blob: a.blob,
              blob_id: a.blob?.id,
              blob_key: a.blob?.key,
              attach_id: a.id,
            }));
          }
        } else {
          if (isQuoteEditable) {
            // 编辑场景下，收款账户无值时设置默认值为可选列表的第一个
            try {
              const { vendorId } = getVendorStorage();
              if (vendorId) {
                const bankRes = await outsourceNewSupplierQueryVmdmBankAccount({ mdm_code: vendorId });
                const bankOptions = bankRes?.data || [];

                if (bankOptions.length > 0) {
                  patch.channel_supplier_bank_dto = bankOptions[0];
                }
              }
            } catch (e) {
              console.warn('获取银行账户默认值失败', e);
            }
          }
        }
      }

      if (Object.keys(patch).length) quoteForm.setFieldsValue(patch);
    };

    fetchAndMerge();
  }, [quoteData, detail?.bidding_suppliers]);

  const prefixClx = 'bidding-detail-all';
  const baseInfoFormProps = {
    detail,
    editFields,
    ref: baseFormRef,
  };
  const tenderFormProps = {
    detail,
    editFields,
    ref: tenderFormRef,
  };
  const evaluationFormProps = {
    detail,
    editFields,
    ref: evaluationFormRef,
    structured: detail?.structured,
  };

  // 兼容适配：详情接口 structured 字段（1 为新结构化报价，0 为旧非结构化报价）
  // 关联 epic-root: mixspec/changes/845-M04-供应商端报价
  const isStructured = (detail?.structured ?? 0) === 1;
  // 编辑态判定：仅"待提交标书"状态展示编辑表单，其他状态只读
  const isQuoteEditable = isStructured && (detail?.state === NodeEnum.AWAITING_SUBMISSION_BIDS || detail?.state === NodeEnum.BID_OPEN_REVIEW) && canEditQuote !== false;

  const { optionsMap } = useDemandOptions({
    keys: ['invoice_type'],
    params: {
      is_oversea_invoice: isOverseaInvoice,
    },
    cache: false,
  });

  // 报价信息模块字段定义，字段集严格按 Figma 设计稿
  // node-id=34178-571287：方案提交下报价信息（收款账户/合同联系人/发票四件套/报价总金额/报价总明细/报价文件）
  // 关联契约: mixspec/changes/845-M04-供应商端报价（bidding_supplier 新增 6 字段 + 报价总明细多 Tab）
  const quotationSummaryFormItem = [
    {
      key: 'bank_account',
      label: '收款账户',
      formItemKey: 'channel_supplier_bank_dto',
      view: !isQuoteEditable,
      span: 1,
      required: true,
      watchChildFormValidate: true,
      render: () => (
        <BankAccount mdm_code={getVendorStorage()?.vendorId} view={!isQuoteEditable} showAddBank required />
      ),
    },
    {
      key: 'contract_contact_info',
      label: '合同联系人信息',
      formItemKey: 'contract_contact_info',
      view: true,
      span: 1,
      required: true,
      render: (renderData: any) => {
        const biddingSuppliers = renderData?.detailData?.bidding_suppliers;
        const info = biddingSuppliers?.[0]?.contract_contact_info;
        // contract_contact_info 为对象 { contact_name, contact_phone, contact_email, contact_address }
        // 服务端动态从 ChannelSupplier 主数据获取，前端只读展示；修改跳转外包端公司管理
        const fields = [
          { label: '联系人姓名', value: info?.contact_name },
          { label: '联系方式', value: info?.contact_phone },
          { label: '联系人邮箱', value: info?.contact_email },
          { label: '联系人地址', value: info?.contact_address },
        ];
        const hasData = fields.some((f) => f.value);
        return (
          <div>
            {hasData ? (
              <div
                style={{
                  border: '1px solid #e4e5eb',
                  borderRadius: 8,
                  padding: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  fontSize: 14,
                  lineHeight: '22px',
                }}>
                {fields.map((f) => (
                  <div key={f.label} style={{ display: 'flex' }}>
                    <span style={{ color: '#68768f', whiteSpace: 'nowrap' }}>{f.label}：</span>
                    <span style={{ color: '#0c1e3d' }}>{f.value || '--'}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span style={{ color: '#68768f' }}>--</span>
            )}
            <Button
              type="link"
              size="small"
              icon={<ChevronRight />}
              onClick={() => {
                window.open('/#/outCompanyManagement/outCompanyInfo');
              }}>
              前往修改
            </Button>
          </div>
        );
      },
    },
    {
      key: 'invoice_belong_area',
      label: '发票国家/地区',
      formItemKey: 'invoice_belong_area',
      componentClass: 'select',
      view: !isQuoteEditable,
      span: 1,
      required: true,
      elementProps: {
        options: enums.InvoiceBelongAreaEnum.values(),
        onChange: (value: number) => {
          setInvoiceBelongArea(value);
          if (value !== 1) {
            quoteForm.setFieldsValue({ is_oversea_invoice: 1, invoice_type: '' });
            setIsOverseaInvoice(1);
          } else {
            quoteForm.setFieldsValue({ is_oversea_invoice: 0, invoice_type: '' });
            setIsOverseaInvoice(0);
          }
        },
      },
    },
    {
      key: 'invoice_type',
      label: '发票类型',
      formItemKey: 'invoice_type',
      componentClass: 'select',
      view: !isQuoteEditable,
      span: 1,
      required: true,
      elementProps: {
        options: optionsMap?.invoice_type?.options,
      },
    },
    {
      key: 'is_oversea_invoice',
      label: '是否海外INVOICE',
      formItemKey: 'is_oversea_invoice',
      componentClass: 'radio',
      view: !isQuoteEditable,
      span: 1,
      required: true,
      elementProps: {
        options: [
          { name: '是', value: 1 },
          { name: '否', value: 0 },
        ],
        disabled: invoiceBelongArea !== 1,
        onChange: (e) => {
          setIsOverseaInvoice(e);
        },
      },
    },
    {
      key: 'total_quoted',
      label: '报价总金额',
      formItemKey: 'total_quoted',
      componentClass: 'currencyType',
      view: !isQuoteEditable,
      span: 1,
      required: true,
    },
    {
      key: 'quote_detail',
      label: '报价总明细',
      formItemKey: 'quote_detail',
      view: !isQuoteEditable,
      span: 1,
      required: true,
      render: (renderData: any) => (
        <QuoteDetailField
          detail={detail}
          view={!isQuoteEditable}
          onChange={(val) => {
            const tabs: any[] = val || [];
            const total = tabs.reduce((sum, tab) => {
              const rows = tab.rows ?? [];
              return rows.reduce((s: Decimal, row: any) => {
                const v = row?.priceAmount;
                return v !== undefined && v !== null && v !== '' ? s.plus(new Decimal(v)) : s;
              }, sum);
            }, new Decimal(0));
            const total_quoted = quoteForm.getFieldValue('total_quoted') ?? {};
            quoteForm.setFieldsValue({
              quote_detail: val,
              total_quoted: { ...total_quoted, value: total.toNumber() },
            });
          }}
        />
      ),
    },
    {
      key: 'bid_attachs',
      label: '报价文件',
      formItemKey: 'bid_attachs',
      componentClass: 'upload',
      view: !isQuoteEditable,
      elementProps: {
        onPreview: undefined,
      },
      span: 1,
    },
  ];

  const renderTipTag = () => {
    if (typeof hasSubmitted !== 'boolean' || detail?.state !== NodeEnum.AWAITING_SUBMISSION_BIDS) {
      return null;
    }
    return hasSubmitted ? (
      <BTag color="light-green" text="已提交" size="xSmall" style={{ marginLeft: 6 }} />
    ) : (
      <BTag color="light-orange" text="待提交" size="xSmall" style={{ marginLeft: 6 }} />
    );
  };

  const validData = async () => {
    try {
      if (baseFormRef.current && tenderFormRef.current) {
        const baseFormValues = baseFormRef.current.validData();
        const tenderFormValues = tenderFormRef.current.validData();
        const evaluationValues = evaluationFormRef.current.validData();
        if (baseFormValues && tenderFormValues && evaluationValues) {
          if (isStructured) {
            const quoteFormValues = await quoteForm.validateFields();
            if (!quoteFormValues) return false;
            return {
              baseFormValues,
              tenderFormValues,
              evaluationValues,
              quoteFormValues,
            };
          }
          return {
            baseFormValues,
            tenderFormValues,
            evaluationValues,
          };
        }
      }
    } catch (e) {
      Message.error(e?.errorFields?.[0]?.errors?.[0] ?? '表单校验失败');
      return false;
    }

    return false;
  };

  const openBidingHistory = useBidingHistoryModal({
    isInner: false,
  });

  let opers = null;
  if (renderOpers) {
    opers = renderOpers(validData);
  }

  const showTurn = detail.turns_number > 1;

  return (
    <div className={prefixClx}>
      <DetailLayoutWithTabs detail={detail} opers={opers}>
        <BlockTitle style={{ marginTop: 20 }}>基本信息</BlockTitle>
        <div style={{ padding: 20, paddingTop: 4 }}>
          <BaseInfoForm key={detail.id} {...baseInfoFormProps} />
        </div>
        <BlockTitle style={{ marginTop: 20 }}>
          方案提交
          {renderTipTag()}
          {showTurn && (
            <>
              <BTag border={true} color={'light-brand'} style={{ marginLeft: 6 }} text={`${detail.turns_number} 轮`} />
              <BLink
                style={{ marginLeft: 16 }}
                type={'primary'}
                onClick={(e) => {
                  openBidingHistory({
                    biddingId: detail.id,
                    supplierId: detail.current_supplier_id,
                    supplierName: '',
                  });
                  e.preventDefault();
                }}>
                查看历史报价轮次
              </BLink>
            </>
          )}
        </BlockTitle>
        <div style={{ padding: 20, paddingTop: 4 }}>
          <EvaluationForm key={detail.id} {...evaluationFormProps} />
        </div>
        {isStructured && (
          <>
            <BlockTitle style={{ marginTop: 20 }}>
              <span>报价信息</span>
            </BlockTitle>
            <div style={{ padding: 20, paddingTop: 16 }}>
              <Form form={quoteForm} onValuesChange={() => {}}>
                <DescriptionsFormItem
                  column={2}
                  items={quotationSummaryFormItem
                    .filter((item) => !(item.key === 'quote_detail' && !quoteData?.tabs?.length))
                    .map((item) =>
                      item.key === 'bid_attachs' && !quoteData?.tabs?.length
                        ? { ...item, required: true }
                        : item,
                    )
                  }
                  renderData={{ detailData: detail }}
                />
              </Form>
            </div>
          </>
        )}
        <BlockTitle style={{ marginTop: 20 }}>比选信息</BlockTitle>
        <div style={{ padding: 20, paddingTop: 4 }}>
          <TenderForm key={detail.id} {...tenderFormProps} />
        </div>
      </DetailLayoutWithTabs>
    </div>
  );
};

export default Template;
